import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import BaseController from "../../common/base/BaseController";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtSearchBar from "../../components/NtSearchBar";
import NtModal from "../../components/NtModal";
import {
  asyncStorageSetData,
  showToast,
} from "../../common/utils/ControllerUtils";
import ringgroupcsvcolumndata from "../data/ringgroupcsvcolumndata";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import { fetchRingGroups } from "../../api/ringgroup";
import NtRingGroupItem from "../components/NtRingGroupItem";
import NtRingGroupAdvancedSearchPicker from "../components/NtRingGroupAdvanceSearchPicker";
import NtRingGroupNewForm from "../components/NtRingGroupNewForm";

class RingGroupController extends BaseController {
  state = {
    ...super.state,
    loading: false,
    data: [],
    totalItems: 0,
    searchText: "",
    searchFilter: null,
    page: 1,
  };

  column_location = "@ringgroup_column_storage";
  addAccessModalRef = React.createRef();
  advanceSearchRef = React.createRef();

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      ringgroupcsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }


  handleDataFetch = async () => {
    this.setState({ loading: true });
    const response = await fetchRingGroups(
      this.context.user?.domain,
      this.state.page,
      settings.pageSizeLoad,
      this.state.searchText,
      this.state.searchFilter,
    );
    Logger("RingGroups fetch response", response);
    if (response.ok) {
      this.setState({
        data: response.data?.data || [],
        totalItems: response.data?.total || 0,
      });
    }
    this.setState({ loading: false });
  };

  handleExportFetch = async () => {
    const response = await fetchRingGroups(
      this.context.user?.domain,
      1, 10000000,
      this.state.searchText,
      this.state.searchFilter,
    );
    return response.ok ? response.data?.data || [] : [];
  };

  handleExportDataParce = (item, column) => {
    if (column === "ring_group_call_timeout") return `${item[column]}s`;
    if (column === "ring_group_enabled" || column === "ring_group_forward_enabled") {
      return item[column] ? "Enabled" : "Disabled";
    }
    if (column === "destination_numbers") {
      const dests = item[column]?.join(",") || "";
      return `"${dests}"`;
    }
    return item[column];
  };

  handleSearch = (text) => {
    this.setState({ searchText: text, page: 1 }, () => this.handleDataFetch());
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, () => this.handleDataFetch());
  };

  renderAdvanceSearchForm = () => (
    <NtModal ref={this.advanceSearchRef}>
      <NtRingGroupAdvancedSearchPicker
        filter={this.state.searchFilter}
        onCancel={() => this.advanceSearchRef.current?.dismiss()}
        onFilter={(filter) => {
          this.setState({ searchFilter: filter, page: 1 }, () => this.handleDataFetch());
          this.advanceSearchRef.current?.dismiss();
        }}
        onFilterClear={() => {
          this.setState({ searchFilter: null, page: 1 }, () => this.handleDataFetch());
          this.advanceSearchRef.current?.dismiss();
        }}
      />
    </NtModal>
  );

  renderAddNewForm = () => (
    <NtModal ref={this.addAccessModalRef}>
      <NtRingGroupNewForm
        onCancel={() => this.addAccessModalRef.current?.dismiss()}
        onCreate={() => {
          this.setState({ page: 1 }, () => this.handleDataFetch());
          this.addAccessModalRef.current?.dismiss();
          showToast("Success", "Successfully created the new ring group");
        }}
      />
    </NtModal>
  );

  renderRow = (item, index) => (
    <NtRingGroupItem
      key={index}
      item={item}
      columns={this.state.columns}
      onUpdate={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", `Successfully updated Ring Group ${item?.ring_group_extension}`);
      }}
      onDelete={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", `Successfully deleted Ring Group ${item?.ring_group_extension}`);
      }}
    />
  );

  render() {
    const canEdit = this.handlePermissionCheck(settings.permissionTypeEdit);
    const count = this.state.totalItems || 0;
    const subtitle = `Distribute incoming calls to multiple extensions · ${count} ring group${count === 1 ? "" : "s"}`;

    const actions = canEdit ? (
      <NtPageActionButton
        icon="plus"
        title="Add Ring Group"
        variant="primary"
        onPress={() => this.addAccessModalRef.current?.show()}
      />
    ) : null;

    const searchBar = (
      <NtSearchBar
        filterCount={this.state.searchFilter ? Object.keys(this.state.searchFilter).length : 0}
        value={this.state.searchText}
        onChangeText={this.handleSearch}
        onFilterPress={() => this.advanceSearchRef?.current?.show()}
      />
    );

    return (
      <NtRootPageContainer
        pageTitle="Ring Groups"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection
          search={searchBar}
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={settings.pageSizeLoad}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: canEdit ? 35 : 0 }}
              columns={ringgroupcsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="ringgroup"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAddNewForm()}
        {this.renderAdvanceSearchForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default RingGroupController;
